import { FC, useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Flex } from 'antd';
import styled from 'styled-components';
import { AddRemoveButtons } from '../../../common/components';
import {
  FormField,
  FormSelect,
  useAdditionalFormContext,
} from '../../../common/form';
import { ROW_UNIQUE_KEY_NAME } from '../../../common/utils';
import { useAlgorithmFormValues } from '../useAlgorithmFormValues';
import {
  AlgorithmJoinItemFormFields,
  AlgorithmJoinItemFormFieldsRef2,
} from './AlgorithmJoinItemFormFields';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import { AlgorithmJoinSourceSelectField } from './AlgorithmJoinSourceSelectField';
import { AlgorithmJoinsFormRowControls } from './AlgorithmJoinsFormRowControls';
import { AutoJoinButton } from './AutoJoinButton';
import {
  getDefaultAlgorithmJoinItemFormData,
  getSubsequentAlgorithmJoinDefaultFormData,
} from './schema/defaultValues';
import { useUsedAlgorithmSourceIds } from './useUsedAlgorithmSourceIds';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmJoinFormFields.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-form-fields',
);
addTranslations(translations);

export interface AlgorithmJoinFormFieldsProps {
  index: number;
  count: number;
  isLast: boolean;
}

export const AlgorithmJoinFormFields: FC<AlgorithmJoinFormFieldsProps> = (
  props,
) =>
  props.index === 0 ? (
    <Flex gap="small">
      <AlgorithmJoinSourceSelectField index={props.index} />
      <AlgorithmJoinsFormRowControls>
        <AddRemoveJoinButtons index={props.index} isLast={props.isLast} />
      </AlgorithmJoinsFormRowControls>
    </Flex>
  ) : (
    <AlgorithmSubsequentJoinFormFields {...props} />
  );

const AlgorithmSubsequentJoinFormFields: FC<AlgorithmJoinFormFieldsProps> = (
  props,
) => {
  const { index, isLast } = props;
  const { t } = useTranslation();

  const [joinTypeOptions] = AlgorithmJoinsFormStore.useValue('joinTypeOptions');
  const [showAutoJoin] = AlgorithmJoinsFormStore.useValue('showAutoJoin');

  const [algorithmSourceId, algorithmJoinItems] = useAlgorithmFormValues([
    `joins.${index}.algorithmSourceId`,
    `joins.${index}.algorithmJoinItems`,
  ] as const);
  const { setValue } = useFormContext();

  const isAnyFilled = useMemo(
    () =>
      algorithmJoinItems.some(
        (i) =>
          ('sourceValue' in i && i.sourceValue) ||
          ('targetValue' in i && i.targetValue) ||
          ('sourceDimension' in i && i.sourceDimension) ||
          ('targetDimension' in i && i.targetDimension) ||
          ('algorithmMappingAlias' in i && i.algorithmMappingAlias),
      ),
    [algorithmJoinItems],
  );

  const firstJoinItemFormFieldsRef =
    useRef<AlgorithmJoinItemFormFieldsRef2>(null);

  const handleSourceChange = useCallback(() => {
    setValue(
      `joins.${index}.algorithmJoinItems`,
      [getDefaultAlgorithmJoinItemFormData()],
      { shouldDirty: true },
    );
    queueMicrotask(() => {
      firstJoinItemFormFieldsRef.current?.autoPairSubjectDimensionsIfPossible();
    });
  }, [setValue, index]);

  return (
    <Flex vertical gap="large">
      <Flex gap="small">
        <FormField
          required
          name={`joins.${index}.joinTypeCode`}
          label={t('join')}
        >
          <FormSelect
            name={`joins.${index}.joinTypeCode`}
            options={joinTypeOptions}
          />
        </FormField>
        <AlgorithmJoinSourceSelectField
          index={index}
          onChange={handleSourceChange}
        />
        <AlgorithmJoinsFormRowControls>
          {showAutoJoin && (
            <AutoJoinButton
              joinIndex={index}
              disabled={!algorithmSourceId || isAnyFilled}
            />
          )}
          <AddRemoveJoinButtons index={index} isLast={isLast} />
        </AlgorithmJoinsFormRowControls>
      </Flex>
      <AlgorithmJoinItemsWrapper vertical gap="middle">
        {algorithmJoinItems?.map((algorithmJoinItem, index, { length }) => (
          <AlgorithmJoinItemFormFields
            ref={index === 0 ? firstJoinItemFormFieldsRef : undefined}
            key={algorithmJoinItem[ROW_UNIQUE_KEY_NAME]}
            joinIndex={props.index}
            index={index}
            count={length}
            isLast={index === length - 1}
          />
        ))}
      </AlgorithmJoinItemsWrapper>
    </Flex>
  );
};

const INDENT_SIZE = 20;

const AlgorithmJoinItemsWrapper = styled(Flex)`
  margin-left: ${INDENT_SIZE}px;
`;

interface AddRemoveJoinButtonsProps {
  index: number;
  isLast: boolean;
}

const AddRemoveJoinButtons: FC<AddRemoveJoinButtonsProps> = (props) => {
  const { index, isLast } = props;
  const { t } = useTranslation();
  const [sources] = AlgorithmJoinsFormStore.useValue('sources');
  const { setValue, getValues, clearErrors } = useFormContext();
  const { onChange: formOnChange } = useAdditionalFormContext();

  const addAlgorithmJoin = useCallback(() => {
    setValue(
      `joins.${index + 1}`,
      getSubsequentAlgorithmJoinDefaultFormData(),
      { shouldDirty: true },
    );
    formOnChange?.();
  }, [setValue, index, formOnChange]);

  const removeAlgorithmJoin = useCallback(() => {
    clearErrors(`joins.${index}`);
    const newJoins = getValues('joins').toSpliced(index, 1);

    // Ensure that the first join is always like a root join should be (with no join type and no algorithm join items)
    if (newJoins.length) {
      newJoins[0] = {
        ...newJoins[0],
        joinTypeCode: null,
        algorithmJoinItems: [],
      };
    }

    setValue('joins', newJoins, {
      shouldDirty: true,
    });

    formOnChange?.();
  }, [setValue, index, getValues, clearErrors, formOnChange]);

  const isAllAlgorithmSourcesUsed =
    sources.length === useUsedAlgorithmSourceIds().length;

  return (
    <AddRemoveButtons
      id={`algorithmJoin-control-${index}`}
      rowName={t('join')}
      onClickAdd={isLast ? addAlgorithmJoin : undefined}
      onClickRemove={removeAlgorithmJoin}
      isAddDisabled={isAllAlgorithmSourcesUsed && t('add-disabled')}
    />
  );
};
